/* global chrome, cast */ // The Cast sender SDK, loaded by its own script tag.
import { Controller } from "stimulus";
import Hls from "hls.js";
import mux from "mux-embed";
import Plyr from "@supercast/plyr";
import { SupercastTimeUpdateEvent } from "utils/events";
import { maybeConnectBridge } from "utils/hotwire";
import { sendPlaybackBreadcrumb, sendPlaybackError } from "utils/playback_telemetry";

/** 🚨🚨🚨🚨 WARNING: This controller has bridge component counterparts. 🚨🚨🚨🚨
 *
 * This means that the functionality provided below is handled natively in the mobile apps.
 * Ensure that any changes to this controller are also reflected in the bridge component counterparts, and
 * tested in both Android and iOS.
 *
 * You can find the bridge component counterparts (one per stream type) in:
 * app/components/media/bridge/video_player_controller.js
 * app/components/media/bridge/live_player_controller.js
 * app/components/media/bridge/premiere_player_controller.js
 */

// How close to the party playhead counts as "caught up" - fast-forward is disabled inside this band.
const premiereAtEdgeToleranceSeconds = 1;

// AWS HLS only (see awsHlsValue): the master manifest only lists rungs that have actually
// finished encoding, so a session that starts before every rung is done won't see a later one appear on its own
// - this polls the master manifest directly and does a clean reload when it's grown.
const masterManifestPollIntervalMs = 45_000;
const masterManifestMaxPollAttempts = 40; // ~30 minutes total - stop rather than poll forever if a rung never finishes
const masterManifestRungTotal = 5;

// hls.js fatal NETWORK_ERROR retry ladder: 1s, 2s, 4s, 8s, 8s, 8s - a ~30 second budget. Any
// successfully loaded fragment resets it (see FRAG_LOADED in createHls), so this is per-blip
// rather than per-session: the old 3-attempt/6-second ladder let one brief connectivity blip
// permanently kill a 2.5-hour premiere 29 minutes in.
//
// Deliberately the same budget for live, even though live's terminal action is the cheap page
// reload rather than an MP4 fallback, so a genuine live failure now black-screens ~30s instead
// of ~6s. That is the accepted trade: the reload re-initialises chat, tips and polls, so paying
// it for a transient blip is worse for the viewer than a longer buffering spinner.
const hlsNetworkErrorMaxRetries = 6;
const hlsNetworkErrorMaxBackoffMs = 8_000;

// Which playback branch of loadHlsUrl() produced the Mux view. Tracked so the hls.js
// instrumentation is only bound on the branch that actually attached it, and so the
// deliberate-swap helpers (muxRemoveHls/muxAddHls) know whether they apply at all.
const muxBranchHlsJs = "hlsjs";
const muxBranchNativeHls = "native-hls";
const muxBranchProgressive = "progressive-mp4";

// Session cookie, so theater survives Turbo navigations and is shared across tabs, but dies with
// the browser. Raw document.cookie because js-cookie is only vendored for the sprockets pipeline.
// Must match CookieHelper::THEATER_MODE - nothing enforces the two stay in sync.
const THEATER_COOKIE = "theater_mode";

function theaterCookieEnabled() {
  return document.cookie.split(";").some(c => c.trim() === `${THEATER_COOKIE}=1`);
}

function writeTheaterCookie(enabled) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${THEATER_COOKIE}=${enabled ? "1" : "0"}; path=/; SameSite=Lax${secure}`;
}

export default class extends Controller {
  static targets = [
    "controlsTemplate",
    "videoContainer",
    "popoutPlaceholder",
    "castPlaceholder",
    "castButton",
    "floatingButton",
    "nonFloatingButton",
    "theaterButton",
    "playback1",
    "playback1_5",
    "playback2"
  ]

  static values = {
    timestamp: Number,
    markers: Array,
    floating: Boolean,
    fileType: String,
    autoplay: Boolean,
    allowNativePlay: Boolean,
    cdn: String,
    videoTitle: String,
    // Mux's video_id, generated server-side (Playback::VideoId) and shared byte-for-byte with
    // the native bridge payloads. Replaces an `episodeId` value no template ever rendered, which
    // made every web view report video_id: undefined.
    videoId: String,
    streamType: String,
    liveUrl: String,
    muxEnvKey: String,
    publishedAtMs: Number,
    serverNowMs: Number,
    duration: Number,
    awsHls: Boolean,
    castPayloadUrl: String,
    castEventId: Number,
    castContentType: String,
    blankVideoUrl: String,
    // :playback_observability. The component only renders these when the flag is on for the
    // entity, so observabilityValue defaulting to false is what keeps the un-flagged player
    // byte-for-byte what it was: no extra Mux fields, no AppSignal traffic, no breadcrumbs.
    observability: Boolean,
    viewerId: String,
    subPropertyId: String,
    planTier: String,
    // Resolved server-side ("aws_hls" | "bunny" | "ivs") rather than inferred from awsHlsValue,
    // which cannot tell bunny from ivs. Absent expiresAtMs means "no advertised deadline", which
    // is not the same as expired - see tokenExpiryTags().
    tokenType: String,
    expiresAtMs: Number,
    // AWS only - the dynamically-created <track>'s attributes.
    captionsUrl: String,
    captionsSrclang: String,
    captionsLabel: String
  }

  initialize() {
    this.requestWakeLock = this.acquireWakeLock.bind(this);
    this.releaseWakeLock = this.releaseWakeLock.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  async connect() {
    if (this.allowNativePlayValue && maybeConnectBridge(this.element, this.bridgeComponent(), this.bridgeControllerName())) {
      return;
    }

    // Inside the native shell this player is always CSS-hidden (hotwire-native:hidden), so
    // when the hand-off to native didn't happen (old binary without the bridge component,
    // or native play disabled) there is nothing to show: skip web init entirely rather than
    // buffering HLS segments and firing Mux QoS beacons behind an invisible element.
    if (document.querySelector("[data-hotwire-native]")) {
      return;
    }

    this.muxPlayerInitTime = mux.utils.now();
    this.video = this.element.querySelector('video');

    // Snapshot clock skew once so the shared playhead stays on server time.
    if (this.isPremiere()) {
      this.clockOffsetMs = this.serverNowMsValue - Date.now();
    }

    this.initializePlayer();

    if (this.isPremiere()) {
      this.premiereClampForwardSeeks();
      this.startPremiereEdgeUpdates();
    }
    this.boundReadyToPlay = this.readyToPlay.bind(this);
    this.boundReloadOnceForLive = () => this.reloadOnceForLive();

    if (!this.isFloating()) {
      this.floatingButtonTargets.forEach(button => button.classList.add("hidden"));
      this.nonFloatingButtonTargets.forEach(button => button.classList.remove("hidden"));
    } else {
      this.floatingButtonTargets.forEach(button => button.classList.remove("hidden"));
      this.nonFloatingButtonTargets.forEach(button => button.classList.add("hidden"));
      // toggleTheater() no-ops while floating, so the control must not be left visible-but-dead
      // in the small floating player - "!hidden" overrides its own "lg:inline-flex" without
      // disturbing that responsive class (unlike the shared nonFloatingButton "hidden" toggle above).
      this.theaterButtonTargets.forEach(button => button.classList.add("!hidden"));
    }

    this.syncTheaterState();

    this.boundOnTimeUpdate = this.onTimeUpdate.bind(this);
    window.addEventListener("supercast-timeupdate", this.boundOnTimeUpdate);
    // The persistent mini audio player outlives navigation; once a video really starts, silence it.
    this.video.addEventListener("play", this.pauseMiniAudioPlayer);
    this.boundRestoreTheaterState = this.restoreTheaterState.bind(this);
    window.addEventListener("pageshow", this.boundRestoreTheaterState);

    if (!this.video.src) {
      this.initializeVideo();
      if (this.hasCastButtonTarget) {
        this.initChromecast();
      }
    }
  }

  isLive() {
    return this.streamTypeValue === "live";
  }

  // VOD path everywhere except the shared-playhead seek + forward clamp below.
  isPremiere() {
    return this.streamTypeValue === "premiere";
  }

  // The bridge component name doubles as the native capability check: old app installs
  // don't declare the live/premiere names, so those pages keep today's behavior there.
  bridgeComponent() {
    if (this.isLive()) return "live-player";
    if (this.isPremiere()) return "premiere-player";
    return "video-player";
  }

  // The bridge controllers live under app/components/media/bridge/, so their
  // glob-registered Stimulus identifier is always media--bridge--<component>.
  bridgeControllerName() {
    return `media--bridge--${this.bridgeComponent()}`;
  }

  serverNow() {
    return Date.now() + this.clockOffsetMs;
  }

  premiereLivePos() {
    return Math.min(Math.max((this.serverNow() - this.publishedAtMsValue) / 1000, 0), this.durationValue);
  }

  // Furthest position a premiere viewer may seek to right now. Saturates at the full duration
  // once the premiere has elapsed, so the afterparty is ordinary free scrubbing.
  premiereMaxSeek() {
    return this.isPremiere() ? this.premiereLivePos() : null;
  }

  // Clamp only forward seeks past the shared edge (rewind/pause OK).
  clampToPremiereEdge() {
    const livePos = this.premiereLivePos();

    // Premiere over: stop clamping.
    if (livePos >= this.durationValue) {
      return;
    }

    if (this.video.currentTime > livePos + 1.5) {
      this.video.currentTime = livePos;
    }
  }

  // Premiere: clamp forward seeks to the shared party playhead, so the jump lands where the
  // 1.5s snap-back clamp would leave it anyway, with no visible bounce. `forward` is a Plyr
  // class field (an own instance property), so overriding it covers every caller at once: the
  // fast-forward button, the ArrowRight keyboard shortcut, and our Media Session handler.
  premiereClampForwardSeeks() {
    const plyrForward = this.player.forward;
    this.player.forward = (seekTime) => {
      const max = this.premiereMaxSeek();
      if (max === null) {
        plyrForward(seekTime);
        return;
      }
      const step = typeof seekTime === "number" ? seekTime : this.player.config.seekTime;
      const current = this.player.currentTime;
      // Floor at current position: if currentTime is already past max (the reactive clamp
      // tolerates up to 1.5s ahead before snapping back), a forward seek must never move
      // the playhead backward.
      this.player.currentTime = Math.max(current, Math.min(current + step, max));
      this.updateFastForwardState();
    };
  }

  // Premiere only: the fast-forward button is dead weight once you are caught up to the party,
  // so disable it there rather than leaving a tap that silently does nothing.
  updateFastForwardState() {
    const button = this.player.elements.buttons?.fastForward;
    const max = this.premiereMaxSeek();
    if (!button || max === null) return;

    const atEdge = max - this.player.currentTime <= premiereAtEdgeToleranceSeconds;
    if (atEdge && document.activeElement === button) {
      // Disabling a focused control force-blurs to <body>, outside Plyr's container-scoped
      // keydown listener, which would silently kill every keyboard shortcut (ArrowRight
      // included). Move focus to the play button first so shortcuts keep working. Uses
      // elements.container, not elements.controls - the latter can go null once the player
      // is relocated into the floating player (see injectControls), the former is the same
      // stable lookup root already used for the PiP button above.
      this.player.elements.container?.querySelector('[data-plyr="play"]')?.focus();
    }
    button.disabled = atEdge;
    button.classList.toggle("opacity-40", atEdge);
    button.classList.toggle("cursor-not-allowed", atEdge);
  }

  startPremiereEdgeUpdates() {
    this.updateFastForwardState();
    this.premiereEdgeInterval = setInterval(() => this.updateFastForwardState(), 1000);
  }

  stopPremiereEdgeUpdates() {
    clearInterval(this.premiereEdgeInterval);
    this.premiereEdgeInterval = null;
  }

  // Live is "purely live" - resuming from a pause snaps to the live edge instead of
  // crawling forward from behind. Only seeks when actually behind, so it's a no-op at the edge.
  seekToLiveEdge() {
    const liveEdge = this.liveEdgePosition();
    if (liveEdge != null && liveEdge - this.video.currentTime > 1) {
      this.video.currentTime = liveEdge;
    }
  }

  liveEdgePosition() {
    if (this.hls && Number.isFinite(this.hls.liveSyncPosition)) {
      return this.hls.liveSyncPosition;
    }
    const seekable = this.video.seekable;
    return seekable && seekable.length > 0 ? seekable.end(seekable.length - 1) : null;
  }

  // Low-latency mode is required for LL-HLS live playback and is a no-op for VOD.
  //
  // xhrSetup: AWS media is served from a CloudFront domain, authorized via signed cookies;
  // cookies only ride along on cross-origin XHRs that opt in via withCredentials.
  // Only opt in when awsHlsValue is true (never for Bunny-backed)
  createHls() {
    const hls = new Hls({
      lowLatencyMode: true,
      backBufferLength: 90,
      // AWS manifests may advertise a subtitle rendition (for native apps/AirPlay/Cast).
      // The web keeps its sideloaded <track> (it predates the manifest track), so hls.js is told to ignore manifest WebVTT
      ...(this.awsHlsValue ? { enableWebVTT: false } : {}),
      // Don't pull a rung bigger than the element it renders into - a 411px phone was spending
      // 76% of a sampled view downscaling the 5 Mbps 1080p rung. hls.js re-evaluates on
      // resize/fullscreen, so going fullscreen still unlocks the higher rungs.
      capLevelToPlayerSize: true,
      xhrSetup: (xhr) => {
        if (this.awsHlsValue) {
          xhr.withCredentials = true;
        }
      }
    });

    this.networkErrorRetries = 0;
    this.mediaErrorRecoveries = 0;
    this.hlsRetryTimeoutId = null;
    hls.on(Hls.Events.ERROR, this.handleHlsError.bind(this));
    // A fragment loading fine means playback actually recovered, not just that hls.js stopped
    // erroring - reset both budgets so a later, unrelated blip gets its own full retry ladder.
    hls.on(Hls.Events.FRAG_LOADED, () => {
      if (this.networkErrorRetries > 0 || this.mediaErrorRecoveries > 0) {
        this.addPlaybackBreadcrumb("hls_recovered", {
          network_retries: this.networkErrorRetries,
          media_recoveries: this.mediaErrorRecoveries
        });
      }
      this.networkErrorRetries = 0;
      this.mediaErrorRecoveries = 0;
    });

    return hls;
  }

  async initializeVideo() {
    this.hls = this.createHls();

    this.video.addEventListener('canplay', this.boundReadyToPlay);

    this.originalUrl = this.element.getAttribute('data-original-url');

    // Live streams have no app playlist/original fallback - load the signed manifest directly.
    if (this.isLive()) {
      this.hlsUrl = this.liveUrlValue;
      this.loadHlsUrl();
      return;
    }

    // Try to fetch the hls playlist url, if we can't find it then fall back to the original
    this.hlsUrl = this.element.getAttribute('data-playlist-url');

    // Premiere's hlsUrl is a cross-origin CDN URL, so a thrown HEAD (CORS/network) must fall back, not hang.
    let response;
    try {
      response = await fetch(this.hlsUrl, { method: 'HEAD', credentials: this.awsHlsValue ? 'include' : 'same-origin' });
    } catch {
      this.loadOriginalUrl("playlist_head_threw");
      return;
    }

    if (response.ok) {
      this.loadHlsUrl();
      if (this.awsHlsValue) {
        this.startMasterManifestPolling();
      }
    } else {
      this.loadOriginalUrl("playlist_head_not_ok", { http_status: response.status });
    }
  }

  loadHlsUrl(withMuxMonitoring = true) {
    /**
     * A note on this conditional: We check if airplay is supported because HLS.js
     * will populate the source of the video with blobs that are not supported by
     * Airplay. Every browser that IS capable of Airplay will support HLS natively though,
     * so we don't need HLS.js in that case.
     *
     * Why not just look for native HLS support and prioritize that? Because some versions
     * of Android have shaky support for HLS, so depsite saying they support it, it causes
     * freezing issues when seeking using native HLS (this is inconsistent so if you're changing
     * this code, test extensively on Android).
     */
    if (Hls.isSupported() && !window.WebKitPlaybackTargetAvailabilityEvent) {
      var video = this.video;
      this.hls.loadSource(this.hlsUrl);
      this.hls.attachMedia(video);
      if (withMuxMonitoring) this.initMuxMonitoring(muxBranchHlsJs);
    } else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
      // Every WebKit browser lands here (Safari + all of iOS) - unmonitored until now, which is why
      // Mux showed zero WebKit views for premieres that certainly had iOS viewers.
      if (withMuxMonitoring) this.initMuxMonitoring(muxBranchNativeHls);
      this.setVideoToHls();
    } else {
      this.loadOriginalUrl("hls_unsupported");
    }
  }

  // Monitoring is attached here rather than only in loadHlsUrl()'s else-branch because this is
  // reached from three other places too (both initializeVideo() fallbacks, a failed
  // setVideoToHls()) - initMuxMonitoring() is idempotent, so the extra calls are no-ops.
  //
  // `reason` exists only for :playback_observability - every fallback here means the viewer is
  // watching a single progressive MP4 instead of adaptive HLS, which is the quiet failure mode
  // behind most "the video keeps buffering" reports, and nothing surfaced it before. Reported
  // first because the live branch at the bottom of this method can unload the page.
  loadOriginalUrl(reason = null, params = {}) {
    if (reason) {
      this.reportPlaybackError("Video player fell back to the original (non-HLS) URL", {
        fallback_reason: reason,
        ...params
      });
    }

    // Live renders no data-original-url, so originalUrl is null here - and `video.src = null`
    // coerces to the literal string "null", which resolves against the page URL and guarantees a
    // MEDIA_ERR_SRC_NOT_SUPPORTED. Skip only the assignment - the Mux calls below still run, so
    // the view is reported either way.
    if (this.originalUrl) this.video.src = this.originalUrl;
    this.initMuxMonitoring(muxBranchProgressive);
    // Already monitoring another branch that fell through to the MP4 (a failed setVideoToHls;
    // recoverFromFatalHlsError has already reported its own swap) - report the change once.
    if (this.muxBranch !== muxBranchProgressive) this.muxSourceChange(muxBranchProgressive);

    // Live with nothing to fall back to: reload instead of leaving a blank player. This is
    // reachable without hls.js ever erroring - on WebKit, loadHlsUrl() takes the native branch and
    // a non-OK signed manifest (expired/revoked playback token) lands here from setVideoToHls(),
    // so handleHlsError/recoverFromFatalHlsError never run and nothing else would ever retry. A
    // reload mints a fresh signed manifest, which is the actual fix for an expired token. Last
    // statement in the method on purpose: the beacons above would otherwise race the unload.
    if (!this.originalUrl && this.isLive()) this.reloadOnceForLive();
  }

  // hls.js's documented recovery ladder: retry network loads, then recover media (swap codec on repeat), then fall back.
  //
  // Each rung leaves a breadcrumb rather than an AppSignal report: a single stall routinely burns
  // two or three rungs and recovers, and reporting each one would drown the real failures. The
  // breadcrumbs ride along on whatever report the bottom of the ladder produces, so one incident
  // tells the whole recovery story.
  handleHlsError(event, data) {
    if (!data.fatal) return;

    if (data.type === Hls.ErrorTypes.NETWORK_ERROR && this.networkErrorRetries < hlsNetworkErrorMaxRetries) {
      this.networkErrorRetries += 1;
      this.addPlaybackBreadcrumb("hls_network_retry", {
        attempt: this.networkErrorRetries,
        details: data.details,
        http_status: data.response && data.response.code
      });
      clearTimeout(this.hlsRetryTimeoutId);
      const backoffMs = Math.min(2 ** (this.networkErrorRetries - 1) * 1000, hlsNetworkErrorMaxBackoffMs);
      this.hlsRetryTimeoutId = setTimeout(() => this.hls.startLoad(), backoffMs);
      return;
    }

    if (data.type === Hls.ErrorTypes.MEDIA_ERROR && this.mediaErrorRecoveries < 2) {
      this.mediaErrorRecoveries += 1;
      if (this.mediaErrorRecoveries === 2) {
        this.addPlaybackBreadcrumb("hls_codec_swap", { details: data.details });
        this.hls.swapAudioCodec();
      } else {
        this.addPlaybackBreadcrumb("hls_media_recovery", {
          attempt: this.mediaErrorRecoveries,
          details: data.details
        });
      }
      this.hls.recoverMediaError();
      return;
    }

    this.recoverFromFatalHlsError(data);
  }

  // Scoped per item (data-item-id, the controller's canonical per-item identity - see also
  // updateFeedTokenConnection), rather than videoIdValue, which is only rendered on the player
  // element itself. A stale reload marker from one broken item can't then silently suppress
  // recovery on an unrelated live/premiere page loaded next in the same tab.
  reloadGuardKey() {
    return `supercast-video-player-reloaded-after-error:${this.element.getAttribute('data-item-id')}`;
  }

  // The single recovery live has: a fresh page mints a fresh signed manifest. Shared by every live
  // failure path (see recoverFromFatalHlsError and loadOriginalUrl) so they cannot each spend their
  // own reload - one broken item gets at most one reload per session, never a loop. The marker is
  // cleared in readyToPlay() once playback actually starts.
  //
  // The guard is spent here rather than in reloadAfterFatalError() so it is claimed even if the
  // reload below is deferred: with :playback_observability on the reload is asynchronous (it
  // waits briefly on the error report), and a second failure arriving inside that window must
  // not start a second reload.
  reloadOnceForLive() {
    if (window.sessionStorage.getItem(this.reloadGuardKey())) return;

    window.sessionStorage.setItem(this.reloadGuardKey(), "1");
    this.reloadAfterFatalError();
  }

  // Recovery exhausted: fall back to original_url (preserving playhead) for premiere/VOD, or a sessionStorage-guarded single reload for live.
  recoverFromFatalHlsError(data = {}) {
    clearTimeout(this.hlsRetryTimeoutId);
    clearTimeout(this.masterManifestPollTimeoutId);

    // Tell Mux the view actually failed. This is the one path that is a genuine playback
    // failure rather than the deliberate teardown/source-swap noise the muxSourceChange helpers
    // exist to keep out of the failure numbers, and it must be emitted before muxRemoveHls() /
    // muxSourceChange() below so it lands on the view that actually failed. Without it the
    // session still looks like a healthy view to Mux's QoS scoring, because the <video> element
    // itself never errored - hls.js swallowed it - and we then quietly swapped the source out
    // from under it.
    this.emitMuxError(data);
    this.reportPlaybackError("Video player exhausted hls.js recovery", {
      hls_error_type: data.type,
      hls_error_details: data.details,
      hls_error_category: this.hlsErrorCategory(data),
      http_status: data.response && data.response.code
    });

    // Returns regardless of whether the guard actually let the reload through: live has no MP4
    // fallback, so falling through to the swap below is never right for it.
    if (this.isLive()) {
      this.reloadOnceForLive();
      return;
    }

    const resumePosition = this.video.currentTime;

    // Deliberate source change to the MP4 fallback, not a playback failure: unbind Mux from the
    // hls.js instance about to be destroyed and start a fresh view for the new source, otherwise
    // mux-embed's automaticErrorTracking reports the swap as a MEDIA_ERR_SRC_NOT_SUPPORTED failure.
    this.muxRemoveHls();
    this.muxSourceChange(muxBranchProgressive);

    this.hls.destroy();
    this.hls = null;
    if (resumePosition > 0) {
      this.video.addEventListener('canplay', () => {
        this.video.currentTime = resumePosition;
      }, { once: true });
    }
    this.loadOriginalUrl("fatal_hls_error");
  }

  // The reload is the recovery, but it also destroys the page before an in-flight report can
  // settle - so with observability on we give the send a short head start. Capped by a race so a
  // hung request can never leave a broken live player on screen, and unchanged (synchronous)
  // when the flag is off.
  reloadAfterFatalError() {
    const pending = this.reportPlaybackError("Video player forced a page reload to recover live playback", {
      recovery: "page_reload"
    });

    if (!pending) {
      window.location.reload();
      return;
    }

    Promise.race([pending, new Promise((resolve) => setTimeout(resolve, 500))])
      .catch(() => {})
      .then(() => window.location.reload());
  }

  async acquireWakeLock() {
      if (!('wakeLock' in navigator) || this.wakeLock) return;

    try {
        console.log('Wake lock requested');
        this.wakeLock = await navigator.wakeLock.request('screen');
        this.wakeLock.addEventListener('release', () => {
          console.log('Wake lock released');
          this.wakeLock = null;
        });
      } catch (error) {
        console.warn('Wake lock failed:', error);
      }
  }

  async releaseWakeLock() {
      await this.wakeLock?.release();
      this.wakeLock = null;
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.acquireWakeLock();
    } else {
      this.releaseWakeLock();
    }
  }

  // Kicks off polling once the initial manifest has loaded successfully - see loadHlsUrl().
  // Grabs its own baseline rung count via a plain fetch (never touching hls.js internals or
  // relying on what hls.js itself parsed), so it starts from a real count instead of assuming 1.
  async startMasterManifestPolling() {
    this.masterManifestPollAttempts = 0;
    this.masterManifestRungCount = await this.fetchManifestRungCount();
    if (this.masterManifestRungCount === null) return; // couldn't read a baseline - don't guess, don't poll

    this.scheduleNextManifestPoll();
  }

  scheduleNextManifestPoll() {
    if (this.masterManifestRungCount >= masterManifestRungTotal) return; // every rung is already there
    if (this.masterManifestPollAttempts >= masterManifestMaxPollAttempts) return; // generous cap reached

    this.masterManifestPollTimeoutId = setTimeout(() => this.pollMasterManifest(), masterManifestPollIntervalMs);
  }

  async pollMasterManifest() {
    this.masterManifestPollAttempts += 1;

    const count = await this.fetchManifestRungCount();
    if (count !== null && count > this.masterManifestRungCount) {
      this.masterManifestRungCount = count;
      this.reloadHlsForNewRungs();
    }

    this.scheduleNextManifestPoll();
  }

  // A plain, uncached GET - counting #EXT-X-STREAM-INF lines is enough to detect growth without
  // parsing the manifest any further. Never throws: a failed fetch just means "try again next tick".
  async fetchManifestRungCount() {
    try {
      const response = await fetch(this.hlsUrl, { credentials: this.awsHlsValue ? 'include' : 'same-origin', cache: 'no-store' });
      if (!response.ok) return null;

      const body = await response.text();
      return (body.match(/#EXT-X-STREAM-INF/g) || []).length;
    } catch {
      return null;
    }
  }

  // A new rung appeared: clean teardown + recreate against the same (now-richer) master URL,
  // restoring playhead/playback state - no inspection of hls.js's level/error internals at all.
  reloadHlsForNewRungs() {
    if (!this.hls) return; // already fell back to loadOriginalUrl (e.g. a fatal error) - nothing to upgrade

    const resumePosition = this.video.currentTime;
    const wasPlaying = !this.video.paused;

    // Read before anything below can move it: muxSourceChange() assigns this.muxBranch, so an
    // unguarded call would flip a native-HLS view to "hlsjs" and then make its own guard - and
    // muxRemoveHls()'s on the next upgrade - wrongly pass.
    const wasOnHlsJsBranch = this.muxBranch === muxBranchHlsJs;

    // Routine rung-ladder reload during a premiere, not an error. Mux was bound to the instance
    // we're replacing, so without the remove/re-add pair the new instance would report nothing;
    // videochange keeps the swap out of the failure numbers. loadHlsUrl(false) below deliberately
    // skips monitor(), which would end this view and start another.
    //
    // All of it is hls.js-branch-only. Polling is started for any awsHls premiere regardless of
    // which branch loadHlsUrl() took, and this.hls is non-null even on the native path
    // (initializeVideo creates it unconditionally), so on WebKit this would otherwise re-label a
    // natively-playing view as "hlsjs" and bind Mux to an instance that was never attachMedia'd.
    // The playback teardown/recreate below still runs on every branch, exactly as before.
    this.muxRemoveHls();

    this.hls.destroy();
    this.hls = this.createHls();

    if (wasOnHlsJsBranch) {
      this.muxSourceChange(muxBranchHlsJs);
      this.muxAddHls();
    }

    this.video.addEventListener('canplay', () => {
      if (resumePosition > 0) this.video.currentTime = resumePosition;
      if (wasPlaying) this.playVideo();
    }, { once: true });

    this.loadHlsUrl(false);
  }

  // Airplay doesn't seem to like following redirects - by prefetching the HLS URL and setting the src to that
  // directly we avoid the redirect and Airplay works as expected.
  async setVideoToHls() {
    const response = await fetch(this.hlsUrl, { credentials: this.awsHlsValue ? 'include' : 'same-origin' });
    if (response.ok) {
      // WebKit re-requests this signed URL whenever it actually loads (a late tap on iOS, an AirPlay
      // handoff), so an expired live token surfaces only as a media error: reload for a fresh one.
      if (this.isLive()) this.video.addEventListener("error", this.boundReloadOnceForLive);
      this.video.src = response.url;
    } else {
      this.loadOriginalUrl("hls_prefetch_not_ok", { http_status: response.status });
    }
  }

  // Idempotent: a second mux.monitor() on the same element tears down the running view (and loses its
  // startup timing) - later source changes go through muxSourceChange() instead.
  initMuxMonitoring(branch) {
    if (this.muxAttached) return;

    this.muxAttached = true;
    this.muxBranch = branch;

    mux.monitor(this.video, {
      debug: false,
      // hls.js instrumentation only belongs on the branch that actually attached it: this.hls is
      // created unconditionally in initializeVideo(), so on the native-HLS/MP4 branches it exists
      // but was never attachMedia'd and would never emit anything.
      ...(branch === muxBranchHlsJs ? { hlsjs: this.hls, Hls: Hls } : {}),
      data: this.muxMetadata()
    });
  }

  // :playback_observability extras. Spread in (rather than inlined above) so that with the flag
  // off this object is empty and the payload Mux receives is exactly the nine fields it received
  // before - a changed dimension set would otherwise split every existing Mux breakdown.
  //
  // Mux's dashboard labels custom dimensions by number only, so the mapping lives here and
  // nowhere else. Do not renumber these: Mux stores them positionally and historical views keep
  // whatever meaning the number had when they were recorded.
  //
  //   custom_1 - subscription plan tier: the viewer's plan name for this entity, or "none" for
  //              followers/logged-out viewers. Lets us see whether a QoS problem tracks a plan.
  //   custom_2 - token type: how playback is authorized/delivered. "aws_hls" (CloudFront signed
  //              cookies + Lambda@Edge), "bunny" (Bunny Stream) or "ivs" (signed IVS manifest).
  //              The single biggest suspect when one cohort's playback degrades.
  //   custom_3 - client surface: "native" or "web".
  //   custom_4..custom_10 - unused, deliberately left free.
  muxObservabilityData() {
    if (!this.observabilityValue) return {};

    try {
      const data = {
        sub_property_id: this.subPropertyIdValue,
        custom_1: this.planTierValue,
        custom_2: this.tokenType(),
        custom_3: this.clientSurface()
      };

      // Absent, not empty: an empty viewer_user_id would merge every logged-out viewer into one
      // fake person in Mux's viewer breakdowns.
      if (this.viewerIdValue) data.viewer_user_id = this.viewerIdValue;

      return data;
    } catch {
      return {};
    }
  }

  // Which delivery/authorization path this session is actually using. Prefers the server's
  // resolved enum (the same string native receives) and only falls back to inferring it from
  // awsHlsValue for a cached page rendered before the attribute existed.
  tokenType() {
    if (this.hasTokenTypeValue && this.tokenTypeValue) return this.tokenTypeValue;
    if (this.isLive()) return "ivs";
    return this.awsHlsValue ? "aws_hls" : "bunny";
  }

  // In practice always "web": connect() hands off to the bridge component, and bails outright,
  // whenever the native shell marker is on the document - so this controller never reaches Mux
  // from inside an app. Computed from the same marker anyway so the dimension stays honest if
  // that early return is ever relaxed.
  clientSurface() {
    return document.querySelector("[data-hotwire-native]") ? "native" : "web";
  }

  hlsErrorCategory(data) {
    if (!data || !data.type) return "unknown";
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) return "network";
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR) return "media";
    return "other";
  }

  // Marks the Mux view as a playback failure. Mux only scores a view as failed if it is told, and
  // hls.js absorbs the underlying error rather than letting the <video> element raise it.
  emitMuxError(data) {
    if (!this.observabilityValue || !this.muxAttached) return;

    try {
      mux.emit(this.video, "error", {
        player_error_code: Number(data && data.response && data.response.code) || 0,
        player_error_message: `${(data && data.type) || "unknown"}: ${(data && data.details) || "unknown"}`
      });
    } catch {
      // Beaconing must never be the reason a fallback source doesn't get loaded.
    }
  }

  // Context attached to every playback report, so an AppSignal incident can be sliced the same
  // way the Mux dashboard is (and joined to it through viewer_id / sub_property_id).
  playbackTags() {
    return {
      cdn: this.cdnValue,
      stream_type: this.streamTypeValue,
      playback_kind: this.isLive() ? "live" : this.isPremiere() ? "premiere" : "vod",
      token_type: this.tokenType(),
      client_surface: this.clientSurface(),
      plan_tier: this.planTierValue,
      sub_property_id: this.subPropertyIdValue,
      viewer_id: this.viewerIdValue,
      item_id: this.element.getAttribute("data-item-id"),
      item_type: this.element.getAttribute("data-item-type"),
      network_retries: this.networkErrorRetries,
      media_recoveries: this.mediaErrorRecoveries,
      ...this.tokenExpiryTags()
    };
  }

  // Mirrors the audio player's tags of the same name. An expired playback credential and a
  // dropped connection both reach us as a fatal hls.js network error with nothing to tell them
  // apart; this says which. "unknown" is a real third answer, not a failure to compute - the
  // web .m3u8 redirect route hides its signed target, so most VOD sessions land here and
  // defaulting them to "expired" would invent an auth incident out of every one.
  //
  // Measured against server time where we have a reference (premiere renders one): a device
  // clock days out would otherwise report every token expired, or hide a real expiry.
  tokenExpiryTags() {
    if (!this.hasExpiresAtMsValue || !this.expiresAtMsValue) return { token_expiry: "unknown" };

    const remainingSeconds = Math.round((this.expiresAtMsValue - (Date.now() + (this.clockOffsetMs || 0))) / 1000);

    return {
      token_expiry: remainingSeconds <= 0 ? "expired" : "valid",
      token_expires_in_s: remainingSeconds
    };
  }

  // Breadcrumbs are accumulated on the shared AppSignal client and attached to the next report it
  // sends, which is exactly the "how did we get here" trail we want on a fatal error. Supported
  // by @appsignal/javascript v1 (addBreadcrumb); frontend custom metrics are not, which is why
  // the retry counts ride along as tags instead of counters.
  addPlaybackBreadcrumb(action, metadata = {}) {
    if (!this.observabilityValue) return;

    sendPlaybackBreadcrumb("video_player", action, { ...metadata, item_id: this.element.getAttribute("data-item-id") });
  }

  // Returns the send Promise (so a caller that is about to navigate away can wait on it), or
  // undefined when the flag is off / the SDK is unavailable.
  reportPlaybackError(message, tags = {}) {
    if (!this.observabilityValue) return undefined;

    return sendPlaybackError(message, {
      action: "media--video-player",
      tags: { ...this.playbackTags(), ...tags }
    });
  }

  // Shared by monitor() and every videochange - an empty videochange payload drops the view's
  // metadata (including startup timing), per mux-embed's docs.
  muxMetadata() {
    return {
      env_key: this.muxEnvKeyValue,
      player_name: "Supercast Video Player",
      player_init_time: this.muxPlayerInitTime,
      // Generated server-side by Playback::VideoId and rendered unconditionally as
      // data-media--video-player-video-id-value ("episode:123"), so the id is assembled in one
      // place rather than by each surface. It replaces episodeIdValue, which was never rendered by
      // the component, so every beacon reported video_id: 0 and errors couldn't be attributed to
      // content at all. The reason to keep the spelling stable is the Mux history already
      // recorded under it, not a contract with any other client.
      //
      // Not data-item-id: that is raw and untyped (it relies on video_content_type to keep an
      // Episode and a LiveStreamSetting with the same id apart), and for live it is the
      // LiveStreamSetting - one Mux row per channel, forever - where the component deliberately
      // reports the LiveEvent so each broadcast is its own view. See Playback::VideoId.
      video_id: this.videoIdValue,
      video_title: this.videoTitleValue,
      video_stream_type: this.streamTypeValue,
      video_cdn: this.cdnValue,
      video_content_type: this.isLive() ? "livestream" : "episode",
      view_client_application_name: "Supercast (Web)",
      ...this.muxObservabilityData()
    };
  }

  // An intentional source swap. Mux treats this as a new view rather than a failure of the old one.
  muxSourceChange(branch) {
    if (!this.muxAttached) return;

    this.muxBranch = branch;
    mux.emit(this.video, "videochange", this.muxMetadata());
  }

  muxRemoveHls() {
    if (!this.muxAttached || this.muxBranch !== muxBranchHlsJs) return;

    mux.removeHLSJS(this.video);
  }

  // Branch-guarded like muxRemoveHls(): mux.addHLSJS is meaningless for a view that isn't playing
  // through hls.js, and this.hls exists on every branch, so the guard belongs here rather than
  // only at the call site. (No equivalent guard can live in muxSourceChange() - it is generic, and
  // the native-hls > progressive-mp4 transitions in loadOriginalUrl()/recoverFromFatalHlsError()
  // are legitimate.)
  muxAddHls() {
    if (!this.muxAttached || this.muxBranch !== muxBranchHlsJs) return;

    mux.addHLSJS(this.video, { hlsjs: this.hls, Hls: Hls });
  }

  // mux.destroyMonitor() emits 'destroy' and unbinds mux's own element listeners (mux-embed 5.18.1).
  muxTeardown() {
    if (!this.muxAttached) return;

    this.muxAttached = false;
    mux.destroyMonitor(this.video);
  }

  initializePlayer() {
    // AWS only: no static <track> is rendered server-side - we know one will exist once 
    // setupAwsCaptionsTrack() creates it, even though the element doesn't exist in the DOM yet
    this.wakeLock = null;
    const hasCaptionsTrack = !!this.video.querySelector("track[kind='captions']") ||
      (this.awsHlsValue && !!this.captionsUrlValue);

    this.player = new Plyr(this.video, {
      autoplay: this.autoplayValue ?? true,
      // Plyr assigns blankVideo on every reset/destroy, so its default
      // (https://cdn.plyr.io/static/blank.mp4) means every teardown hits a third-party CDN we
      // don't control. Self-hosted instead; a blank value leaves Plyr's own default in place.
      ...(this.blankVideoUrlValue ? { blankVideo: this.blankVideoUrlValue } : {}),
      seekTime: 15,
      fullscreen: { enabled: true, iosNative: true },
      volume: 1,
      captions: { active: false, update: true },
      controls: [
        'play-large', // The large play button in the center
        this.isLive() ? null : 'rewind', // Rewind by seekTime (not for live)
        'play', // Play/pause playback
        this.isLive() ? null : 'fast-forward', // Fast forward by seekTime, clamped to the party playhead during premieres (not for live)
        this.isLive() ? null : 'progress', // The progress bar and scrubber (not for live)
        this.isLive() ? null : 'current-time', // The current time of playback (not for live)
        'duration', // Full duration (VOD) / elapsed live time (live)
        'mute', // Toggle mute
        'volume', // Volume control
        'airplay', // Airplay (currently Safari only)
        this.isLive() || this.isPremiere() ? null : 'pip', // Picture-in-picture (not for live/premiere)
        hasCaptionsTrack ? 'captions' : null, // Captions
        'fullscreen', // Toggle fullscreen
      ].flat()
    });

    this.injectControls();
    this.adjustNativeControlIcons();

    this.addMobileEnhancements();
    this.initializePictureInPicture();
    this.setMediaSessionActionHandlers();
    this.setupAwsCaptionsTrack();
    this.setupAirplayCastUrl();
  }

  // AirPlay hands the receiver whatever URL the element is playing, and ours depends on cookies a
  // receiver can't send. For AWS HLS premieres (castPayloadUrlValue present) we therefore swap the
  // element to the cookie-free cast_url BEFORE the picker opens: swapping on 'connect' would be
  // after the handoff already carried the old URL. The cast_url is prefetched once a target is
  // available so the capture-phase click stays synchronous inside the user gesture. Native HLS
  // only (WebKit), never hls.js, whose blob URLs AirPlay can't use. Any failure leaves playback as is.
  setupAirplayCastUrl() {
    if (!this.castPayloadUrlValue || !this.isPremiere() || !window.WebKitPlaybackTargetAvailabilityEvent) return;

    if (this.airplayListeners) return;

    const remote = this.video.remote;
    this.airplayListeners = [
      [this.video, "webkitplaybacktargetavailabilitychanged", (event) => {
        if (event.availability === "available" && !this.airplayCastUrl) {
          this.fetchCastUrl().then((url) => { this.airplayCastUrl = url; }).catch(() => {});
        }
      }],
      [this.element, "click", (event) => {
        if (event.target.closest?.('[data-plyr="airplay"]')) this.swapToCastUrl();
      }, true],
      // Fallback if the click path was skipped (no prefetch yet): the restore below still applies.
      [remote, "connect", () => this.swapToCastUrl()],
      [remote, "disconnect", () => this.restoreFromCastUrl()],
      [this.video, "webkitcurrentplaybacktargetiswirelesschanged", () => {
        if (!this.video.webkitCurrentPlaybackTargetIsWireless) this.restoreFromCastUrl();
      }]
    ].filter(([target]) => target?.addEventListener);
    this.airplayListeners.forEach(([target, type, handler, capture]) => target.addEventListener(type, handler, capture));
  }

  teardownAirplayCastUrl() {
    this.airplayListeners?.forEach(([target, type, handler, capture]) => target.removeEventListener(type, handler, capture));
    this.airplayListeners = null;
  }

  swapToCastUrl() {
    if (!this.airplayCastUrl || this.airplaySwapped) return;

    // Empty when the element plays a MediaSource/blob source; restore then reloads normally.
    this.airplayOriginalSrc = this.video.currentSrc || this.video.src || null;
    this.airplaySwapped = true;
    this.reloadVideoSource(this.airplayCastUrl);
  }

  restoreFromCastUrl() {
    if (!this.airplaySwapped) return;

    const original = this.airplayOriginalSrc;
    this.airplaySwapped = false;
    this.airplayOriginalSrc = null;
    if (original) {
      this.reloadVideoSource(original);
    } else {
      this.loadHlsUrl(false);
    }
  }

  // Native src swap keeping the playhead and play state. Premiere seeking stays clamped by the
  // existing forward-seek guard, which listens on the same element.
  reloadVideoSource(url) {
    const time = this.video.currentTime;
    const wasPlaying = !this.video.paused;
    this.video.addEventListener("loadedmetadata", () => {
      this.video.currentTime = time;
      if (wasPlaying) this.playVideo();
    }, { once: true });
    this.video.src = url;
  }

  // A server-rendered <track> races hls.js's media.src= reset (which clears text-track state)
  // against the browser fetching that <track> early - whichever wins varies per page load. We
  // render no <track> for AWS at all and create one here only after 'loadedmetadata', so there's 
  // no track yet for a reset to wipe.
  setupAwsCaptionsTrack() {
    if (!this.awsHlsValue || !this.captionsUrlValue) return;

    this.video.addEventListener('loadedmetadata', () => {
      // Remove any track created by a previous firing before appending a fresh one
      const stale = this.video.querySelector("track[data-dynamic-aws-captions]");
      if (stale) stale.remove();

      const trackEl = document.createElement('track');
      trackEl.kind = 'captions';
      trackEl.src = this.captionsUrlValue;
      if (this.captionsSrclangValue) trackEl.srclang = this.captionsSrclangValue;
      trackEl.label = this.captionsLabelValue || 'Captions';
      trackEl.default = true;
      // Our own marker to find/remove this exact element later - kind='captions' alone isn't
      // enough to distinguish "the dynamic track we created" from a future unrelated track.
      trackEl.setAttribute('data-dynamic-aws-captions', '');

      this.video.appendChild(trackEl);

      // Plyr's captions.setup() always listens for 'removetrack' on the real TextTrackList,
      // regardless of the captions.update config option above - dispatching it here re-runs 
      // Plyr's own update() logic exactly as if a track had actually been removed, causing it
      // to notice the track we just added.
      this.video.textTracks.dispatchEvent(new Event('removetrack'));
    });
  }

  addMobileEnhancements() {
    // Rely on desktops not having orientation support for detection
    if (!screen.orientation) {
      return;
    }

    this.player.on("enterfullscreen", () => {
      screen.orientation.lock("landscape");
    });

    this.player.on("exitfullscreen", () => {
      screen.orientation.unlock();
    });

    // Live and premiere have no playback-speed control, so their rate-toggle targets are absent.
    if (!this.isLive() && !this.isPremiere()) {
      this.player.on("ratechange", this.onRateChange.bind(this));
    }

    this.video.addEventListener("webkitfullscreenchange", () => {
      if (this.video.webkitDisplayingFullscreen) {
        screen.orientation.lock("landscape");
      } else {
        screen.orientation.unlock();
      }
    });

    this.player.on('play', () => {
      this.requestWakeLock();
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    });

    this.player.on('pause', () => {
      this.releaseWakeLock();
    });

    this.player.on('ended', () => {
      this.releaseWakeLock();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    });
  }

  initializePictureInPicture() {
    if (!document.pictureInPictureEnabled) {
      return;
    }
    this.video.addEventListener("leavepictureinpicture", this.leavePictureInPicture.bind(this));

    this.pipEnabled = true;

    // Chrome requires readyState >= HAVE_METADATA (1) before requestPictureInPicture() can be
    // called. Disable the PiP button until the video element has loaded its metadata, so an
    // eager click before the video is ready doesn't throw an InvalidStateError.
    const pipButton = this.player.elements.container?.querySelector('[data-plyr="pip"]');
    if (pipButton && this.video.readyState < HTMLMediaElement.HAVE_METADATA) {
      pipButton.disabled = true;
      this.video.addEventListener(
        "loadedmetadata",
        () => { pipButton.disabled = false; },
        { once: true }
      );
    }
  }


  injectControls() {
    const controls = document.importNode(this.controlsTemplateTarget.content, true);
    const rewindIcon = controls.querySelector('[data-video-icon="rewind"]');
    const forwardIcon = controls.querySelector('[data-video-icon="fast-forward"]');

    // If relocated, the controls element will be null - this is fine since we've already injected the controls
    if (this.player.elements.controls) {
      this.player.elements.controls.appendChild(controls);
      this.swapSeekIcon('rewind', rewindIcon);
      this.swapSeekIcon('fast-forward', forwardIcon);
    }
  }

  // Plyr renders its native fullscreen button before our appended custom controls - move it
  // to the end so playback-rate/cast/theater sort ahead of it as intended. Also shrink the
  // fullscreen/pip icons: Plyr's stylesheet sizes every control svg via the
  // --plyr-control-icon-size custom property (18px default), which Tailwind classes can't
  // override without !important - setting the variable inline wins the cascade instead, and
  // 12px was confirmed visually to match the weight of the theater/cast icons next to them.
  adjustNativeControlIcons() {
    const fullscreenButton = this.element.querySelector('[data-plyr="fullscreen"]');
    const pipButton = this.element.querySelector('[data-plyr="pip"]');

    if (fullscreenButton) fullscreenButton.parentElement.appendChild(fullscreenButton);
    [fullscreenButton, pipButton].forEach(btn => btn && btn.style.setProperty('--plyr-control-icon-size', '12px'));
  }

  // Plyr renders default double-triangle icons for these from its own CDN sprite -
  // swap in the same custom "15s" icon the audio player uses instead.
  swapSeekIcon(plyrControl, iconHolder) {
    if (!iconHolder) return;
    const button = this.player.elements.controls.querySelector(`[data-plyr="${plyrControl}"]`);
    const svg = button?.querySelector('svg');
    if (svg) {
      svg.replaceWith(iconHolder.querySelector('svg'));
    }
    iconHolder.remove();
  }

  updateTime() {
    const event = new SupercastTimeUpdateEvent({
      itemId: this.element.getAttribute('data-item-id'),
      itemType: this.element.getAttribute('data-item-type'),
      currentTime: this.player.currentTime,
      duration: this.player.duration,
      isPlaying: !this.player.paused,
      mediaType: "video"
    });

    this.updateMediaSessionPlaybackState();

    window.dispatchEvent(event);
  }

  isFloating() {
    return this.element.parentElement.id == "floating-player";
  }

  leavePictureInPicture(event) {
    // Live and premiere expose no PiP control, so there's no feed-item page to return to - leave the player in place.
    if (this.isLive() || this.isPremiere()) {
      return;
    }

    // If users hit the 'X' button (rather than return to frame), the video will be automatically paused beforehand
    // there's no other way to detect the difference. In that case we shouldn't try to navigate back to the video.
    // (This adds an edge case where pausing and then hitting 'return to frame' will not navigate back to the video,
    // but the alternative of navigating on 'X' is worse)
    if (this.player.paused) {
      return;
    }

    if (window.location.pathname != this.thisVideoPath()) {
      this.player.pip = false;
      this.player.pause();
      event.preventDefault();
      window.Turbo.visit(`/subscriber_v2/feed_items/${this.element.getAttribute('data-feed-item-id')}?timestamp=${this.player.currentTime}`);
      this.close();
    }
  }

  disconnect() {
    // First: tearing the player down below fires a media error, which must not reload the page being left.
    this.video?.removeEventListener("error", this.boundReloadOnceForLive);
    this.stopCastPolling();
    this.teardownAirplayCastUrl();
    this.stopPremiereEdgeUpdates();
    this.releaseWakeLock();
    clearTimeout(this.hlsRetryTimeoutId);
    clearTimeout(this.masterManifestPollTimeoutId);
    window.removeEventListener("supercast-timeupdate", this.boundOnTimeUpdate);
    window.removeEventListener("pageshow", this.boundRestoreTheaterState);
    this.video?.removeEventListener("play", this.pauseMiniAudioPlayer);

    // Don't destroy when in PiP - HLS.js must keep running to supply data to the floating window.
    if (!document.pictureInPictureElement || document.pictureInPictureElement !== this.video) {
      // Tell Mux the view is ending deliberately BEFORE the source goes away. Without this, an
      // ordinary Turbo navigation away reads to mux-embed's automaticErrorTracking as a
      // MEDIA_ERR_SRC_NOT_SUPPORTED playback failure, with watch time equal to what was watched -
      // the bulk of the false code-4 failure rate. Inside the PiP guard on purpose: playback
      // continues in the floating window, so ending the view there would be just as wrong.
      this.muxTeardown();
      this.hls?.destroy();
      this.player?.destroy();
    }
  }

  thisVideoPath() {
    return `/subscriber_v2/feed_items/${this.element.getAttribute('data-feed-item-id')}`;
  }

  close() {
    this.element.remove();
  }

  async toggleCast() {
    if (this.isCasting) {
      this.stopCasting();
    } else {
      this.cast();
    }
  }

  async cast() {
    const session = await this.getChromeCastSession(true);
    // AWS HLS: the receiver holds none of this browser's cookies, so the sender fetches the feed item
    // JSON with its own session and hands over only the cookie-free cast_url it carries. Everything
    // else - and any failure here - casts the MP4 redirect as before.
    const castUrl = await this.fetchCastUrl();
    const url = castUrl || this.originalUrl;
    const contentType = castUrl ? this.castContentTypeValue : this.fileTypeValue;
    const mediaInfo = new chrome.cast.media.MediaInfo(url, contentType);
    const request = new chrome.cast.media.LoadRequest(mediaInfo);
    request.autoplay = true;
    session.loadMedia(request);
    this.isCasting = true;

    this.castPlaceholderTarget.classList.remove("hidden");
    this.videoContainerTarget.classList.add("hidden");
    this.releaseWakeLock();
  }

  // Null when there is no payload url to fetch, the fetch fails, or the payload carries no cast_url.
  async fetchCastUrl() {
    if (!this.castPayloadUrlValue) return null;

    try {
      const response = await fetch(this.castPayloadUrlValue, { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (!response.ok) return null;

      const payload = await response.json();
      // /live.json serializes whichever event the page resolves, which may not be this player's.
      if (this.hasCastEventIdValue && payload.id !== this.castEventIdValue) return null;
      return payload.cast_url || null;
    } catch (_error) {
      return null;
    }
  }

  async stopCasting() {
    const castSession = await this.getChromeCastSession(false);
    if (castSession) {
      castSession.endSession(true);
    }
    this.isCasting = false;
    this.castPlaceholderTarget.classList.add("hidden");
    this.videoContainerTarget.classList.remove("hidden");
  }

  // play() rejects when the browser blocks autoplay or a new load interrupts it. The player's own
  // play button covers both, so only those two are expected; anything else still surfaces.
  playVideo() {
    const playing = this.video.play();
    if (!playing) return;
    playing.catch((error) => {
      if (error && (error.name === "NotAllowedError" || error.name === "AbortError")) return;
      throw error;
    });
  }

  // A premiere mounted early behind its countdown (data-action set when the player is held): the page
  // signals T-0 and every viewer starts on the shared playhead with the video already loaded. When it
  // has not finished loading yet, readyToPlay seeks and plays once it has.
  startPremiere() {
    if (!this.video) return;

    this.autoplayValue = true;
    if (!this.premiereLoaded) return;

    this.video.currentTime = this.premiereLivePos();
    this.playVideo();
  }

  // timestampValue is set too so a seek issued before canplay isn't overwritten by readyToPlay's resume.
  seekTo(seconds) {
    this.timestampValue = seconds;
    this.video.currentTime = seconds;
    this.playVideo();
  }

  async readyToPlay() {
    this.video.removeEventListener('canplay', this.boundReadyToPlay);
    window.sessionStorage.removeItem(this.reloadGuardKey());

    // Live streams have no resume position or feed-token tracking - just play.
    if (this.isLive()) {
      this.player.on("play", this.seekToLiveEdge.bind(this));
      if (this.autoplayValue) {
        this.playVideo();
      }
      return;
    }

    // Start at the shared playhead; never resume from playbackDb, so everyone lands on the same moment.
    if (this.isPremiere()) {
      this.premiereLoaded = true;
      this.video.currentTime = this.premiereLivePos();
      this.player.on("timeupdate", this.clampToPremiereEdge.bind(this));
      this.player.on("seeking", this.clampToPremiereEdge.bind(this));
      if (this.autoplayValue) {
        this.playVideo();
      }
      return;
    }

    if (this.hasTimestampValue && this.timestampValue > 0) {
      this.video.currentTime = this.timestampValue;
    } else {
      const listenData = await window.playbackDb.getPlaybackData(this.element.getAttribute('data-item-type'), this.element.getAttribute('data-item-id'));
      if (listenData) {
        this.video.currentTime = listenData.currentTime;
      }
    }

    this.player.on("timeupdate", this.updateTime.bind(this));
    if (this.autoplayValue) {
      this.playVideo();
    }

    // Update feed token connection status when video starts playing
    this.updateFeedTokenConnection();
  }

  updateFeedTokenConnection() {
    const episodeId = this.element.getAttribute('data-item-id');
    if (episodeId) {
      fetch(`/subscriber_v2/feed_items/mark_feed_token_connected?episode_id=${episodeId}`, {
        method: "POST"
      });
    }
  }

  updateMediaSessionPlaybackState() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    navigator.mediaSession.setPositionState({
      duration: this.player.duration,
      playbackRate: this.player.speed,
      position: this.player.currentTime
    });

    navigator.mediaSession.playbackState = this.player.paused ? "paused" : "playing";

  }

  setMediaSessionActionHandlers() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    navigator.mediaSession.setActionHandler("play", () => {
      this.play();
    });

    navigator.mediaSession.setActionHandler("pause", () => {
      this.pause();
    });

    navigator.mediaSession.setActionHandler("seekbackward", () => {
      this.player.currentTime -= 15;
    });

    navigator.mediaSession.setActionHandler("seekforward", () => {
      this.player.forward();
    });
  }

  initChromecast() {
    if (!window.chrome) { return; }
    // Held on the controller so disconnect() can stop polling when the SDK never becomes available.
    // chrome.cast itself loads asynchronously (the sender SDK script tag finishing doesn't mean
    // the namespace exists yet), so it's checked here on every tick rather than once up front -
    // otherwise a cold page load can race the SDK and never see the cast button appear.
    this.loadCastInterval = setInterval(() => {
      if (window.chrome.cast && window.chrome.cast.isAvailable) {
        this.stopCastPolling();
        this.initializeCastApi();
      }
    }, 1000);
  }

  stopCastPolling() {
    clearInterval(this.loadCastInterval);
    this.loadCastInterval = null;
  }

  async initializeCastApi() {
    this.castContext = cast.framework.CastContext.getInstance();
    this.castContext.setOptions({
      receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
    });
    this.castButtonTarget.classList.remove("hidden");
  }

  async getChromeCastSession(initializeNew) {
    const castSession = this.castContext.getCurrentSession();
    if (castSession) {
      return castSession;
    } else if (initializeNew) {
      await this.castContext.requestSession();
      return this.castContext.getCurrentSession();
    } else {
      return null;
    }
  }

  // Arrow property so the same reference can be removed on disconnect.
  pauseMiniAudioPlayer = () => {
    const audioElement = document.querySelector("[data-audio-player]");
    if (!audioElement) return;
    const audio = this.application.getControllerForElementAndIdentifier(audioElement, "media--audio-player");
    if (audio?.player && !audio.player.paused) audio.pause();
  };

  onTimeUpdate(event) {
    if (event.detail.mediaType === "audio" && this.hasFloatingValue && this.floatingValue) {
      this.close();
    }
  }

  // Duplicated from audio player - consider keeping in sync
  onRateChange() {
    if (!this.hasPlayback1Target) return;
    if (this.player.speed == 1) {
      this.playback1Target.classList.remove("!hidden")
      this.playback1_5Target.classList.add("!hidden")
      this.playback2Target.classList.add("!hidden")
    } else if (this.player.speed == 1.5) {
      this.playback1Target.classList.add("!hidden")
      this.playback1_5Target.classList.remove("!hidden")
      this.playback2Target.classList.add("!hidden")
    } else {
      this.playback1Target.classList.add("!hidden")
      this.playback1_5Target.classList.add("!hidden")
      this.playback2Target.classList.remove("!hidden")
    }
  }

  // Duplicated from audio player - consider keeping in sync
  togglePlaybackRate() {
    if (this.player.speed == 1) {
      this.player.speed = 1.5;
    } else if (this.player.speed == 1.5) {
      this.player.speed = 2;
    } else {
      this.player.speed = 1;
    }
  }

  toggleTheater() {
    if (this.isFloating()) return;

    writeTheaterCookie(document.body.toggleAttribute("data-theater"));
  }

  // Turbo restoration visits reuse a cached <body> without re-requesting, so the server-rendered
  // state can be stale on back/forward - re-derive it from the cookie whenever the player attaches.
  syncTheaterState() {
    if (this.isFloating() || !this.hasTheaterButtonTarget) return;

    document.body.toggleAttribute("data-theater", theaterCookieEnabled());
  }

  restoreTheaterState(event) {
    if (event.persisted) this.syncTheaterState();
  }
}
